import { BleClient, numbersToDataView } from '@capacitor-community/bluetooth-le';
import crc8 from 'crc/crc8';

const serviceUUID = '0000ae30-0000-1000-8000-00805f9b34fb';
const writeCharacteristicUUID = '0000ae01-0000-1000-8000-00805f9b34fb';
const notifyCharacteristicUUID = '0000ae02-0000-1000-8000-00805f9b34fb';
const imageCharacteristicUUID = '0000ae03-0000-1000-8000-00805f9b34fb';

const commands = {
    GetStatus: 0xa1,
    PrintIntensity: 0xa2,
    EjectPaper: 0xa3,
    RetractPaper: 0xa4,
    QueryCount: 0xa7,
    Print: 0xa9,
    PrintComplete: 0xaa,
    BatteryLevel: 0xab,
    CancelPrint: 0xac,
    PrintDataFlush: 0xad,
    UnknownAE: 0xae,
    GetPrintType: 0xb0,
    GetVersion: 0xb1,
    UnknownB2: 0xb2,
    UnknownB3: 0xb3
};

const state = {
    connectedDeviceId: null
};

const mutations = {
    setConnectedDeviceId(state, id) {
        state.connectedDeviceId = id;
    },
    clearConnectedDeviceId(state) {
        //console.log('clearConnectedDeviceId');
        state.connectedDeviceId = null;
    }
};

const actions = {
    async printer_scan({ dispatch }) {
        try {
            await BleClient.initialize();

            const targetName = 'MXW01';

            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    BleClient.stopLEScan();
                    reject(new Error('Dispositivo no encontrado'));
                }, 10000); // 10 segundos de espera

                const handleScan = async (result) => {
                    for (const key in result) {
                        if (Object.prototype.hasOwnProperty.call(result, key)) {
                            console.log(`key: ${key} | value:`, result[key]);
                        }
                    }

                    if (result?.localName === targetName) {
                        clearTimeout(timeout);
                        await BleClient.stopLEScan();

                        try {
                            await dispatch('printer_connect', result.device);
                            resolve(result);
                        } catch (err) {
                            reject(err);
                        }
                    }
                };

                BleClient.requestLEScan(
                    { allowDuplicates: false },
                    handleScan
                );
            });

        } catch (e) {
            console.error('Error durante el escaneo:', e);
            throw e;
        }
    },

    async printer_connect({ commit }, device) {
        try {
            await BleClient.connect(device.deviceId, (deviceId) => {
                console.log('Desconectado:', deviceId);
                commit('clearConnectedDeviceId');
            });

            commit('setConnectedDeviceId', device.deviceId);

            await BleClient.startNotifications(
                device.deviceId,
                serviceUUID,
                notifyCharacteristicUUID,
                (value) => {
                    const data = new Uint8Array(value.buffer);
                    const hexString = Array.from(data).map(b => b.toString(16).padStart(2, '0')).join('-').toUpperCase();
                    console.log('Notificación:', hexString);
                }
            );

            console.log('Conectado a:', device.deviceId);
        } catch (e) {
            console.error('Error al conectar:', e);
            throw e;
        }
    },

    printer_formatMessage(_, { commandId, commandData }) {
        const len = commandData.length;
        const len_L = len & 0xff;
        const len_H = (len >> 8) & 0xff;
        const crc = crc8(commandData);
        return [
            0x22, 0x21,
            commandId,
            0x00,
            len_L,
            len_H,
            ...commandData,
            crc,
            0xff
        ];
    },

    async printer_sendCommand({ state }, message) {
        if (!state.connectedDeviceId) return;

        await BleClient.writeWithoutResponse(
            state.connectedDeviceId,
            serviceUUID,
            writeCharacteristicUUID,
            numbersToDataView(message)
        );
    },

    async printer_feedPaper({ dispatch }) {
        const lineCount = 20;
        const commandData = [
            lineCount & 0xff,
            (lineCount >> 8) & 0xff
        ];
        const message = await dispatch('printer_formatMessage', {
            commandId: commands.EjectPaper,
            commandData
        });
        await dispatch('printer_sendCommand', message);
    },

    async printer_printTicket({ state, dispatch }, { blob }) {
        const imageBitmap = await createImageBitmap(blob);
        const width = 384;
        const height = Math.floor(imageBitmap.height * (width / imageBitmap.width));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imageBitmap, 0, 0, width, height);

        const imageData = ctx.getImageData(0, 0, width, height).data;
        const bytesPerLine = width / 8;
        const data = [];

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x += 8) {
                let byte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    if (x + bit >= width) continue;

                    const idx = ((y * width) + x + bit) * 4;
                    const r = imageData[idx];
                    const g = imageData[idx + 1];
                    const b = imageData[idx + 2];
                    const grayscale = 0.3 * r + 0.59 * g + 0.11 * b;
                    const bitValue = grayscale < 128 ? 1 : 0;
                    byte |= (bitValue << bit);
                }
                data.push(byte);
            }
        }

        const lineCount = height;

        const intensityCmd = await dispatch('printer_formatMessage', {
            commandId: commands.PrintIntensity,
            commandData: [100]
        });
        await dispatch('printer_sendCommand', intensityCmd);

        const printCmd = await dispatch('printer_formatMessage', {
            commandId: commands.Print,
            commandData: [
                lineCount & 0xff,
                (lineCount >> 8) & 0xff,
                0x30, 0x00
            ]
        });
        await dispatch('printer_sendCommand', printCmd);

        const linesPerPacket = 4;
        for (let i = 0; i < data.length; i += bytesPerLine * linesPerPacket) {
            const packet = data.slice(i, i + bytesPerLine * linesPerPacket);
            await BleClient.writeWithoutResponse(
                state.connectedDeviceId,
                serviceUUID,
                imageCharacteristicUUID,
                numbersToDataView(packet)
            );
        }

        const flushCmd = await dispatch('printer_formatMessage', {
            commandId: commands.PrintDataFlush,
            commandData: [0x00]
        });
        await dispatch('printer_sendCommand', flushCmd);

        console.log('Ticket enviado');
    }
};

const getters = {
    printerIsConnected: state => !!state.connectedDeviceId,
    printerConnectedDeviceId: state => state.connectedDeviceId
};

export default {
    state,
    mutations,
    actions,
    getters
};
